import { Box, Button, TextField, Typography, useTheme } from '@mui/material';
import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import SurveyService from '../../services/SurveyService';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Autolink from '../Autolink/Autolink';

interface Props {
  companyLogo: string;
  primaryColor: string;
  platform: string;
  selectedRating: number;
  secondaryColor: string;
  companyName: string;
  dataProtection: string;
  imprint: string;
  contactEmail: string;
  companyWebsiteUrl: string;
  customFont: string;
  title?: string;
  outroText?: string;
  contactForm?: boolean;
  introText?: string;
  id: string;
  onChange: (value: string) => void;
  feedbackText?: string;
  feedbackTitle?: string;
  feedbackActive?: boolean;
  lastAnswerId?: string;
}

const NegativeFeedback: FC<Props> = ({
  primaryColor,
  secondaryColor,
  contactEmail,
  customFont,
  companyName,
  title,
  outroText,
  introText,
  contactForm,
  id,
  selectedRating,
  onChange,
  feedbackText,
  feedbackTitle,
  feedbackActive,
  lastAnswerId,
}) => {
  const theme = useTheme();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const maxFeedbackChars = 300;
  const [feedback, setFeedback] = useState<string>('');
  const [sendFeedback, setSendFeedback] = useState<boolean>(false);

  useEffect(() => {
    onChange(feedback);
  }, [feedback]);

  const formatTextToString = (text: any) => {
    if (!text) return null;

    return text.replace(/(\r\n|\r|\n)/g, ' ');
  };

  const sendNpsSurvey = async () => {
    if (lastAnswerId) {
      try {
        await SurveyService.updateNpsSurveyAnswer(
          lastAnswerId,
          id,
          selectedRating,
          contactEmail,
          formatTextToString(feedback),
          companyName
        );
        setSendFeedback(true);
      } catch (error) {
        console.error(error);
      }
    } else {
      console.log('No previous answer id found', lastAnswerId);
    }
  };

  const keyframes = {
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
  };

  const handleFeedbackChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= maxFeedbackChars) {
      setFeedback(e.target.value);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: primaryColor,
          fontWeight: 'bold',
          fontFamily: customFont,
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          color: secondaryColor,
          fontWeight: 'light',
          fontSize: '1.1rem',
          mt: 4,
          mb: 4,
          fontFamily: customFont,
          lineHeight: '1.5',
          textAlign: 'center',
        }}
      >
        <Autolink text={introText} />
      </Typography>
      {companyName && outroText && (
        <Typography
          variant="h5"
          sx={{
            color: secondaryColor,
            fontWeight: 'light',
            mt: theme.spacing(1),
            fontFamily: customFont,
            mb: theme.spacing(4),
          }}
        >
          <Autolink text={outroText} />
        </Typography>
      )}
      {feedbackActive && (
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: secondaryColor,
              pt: theme.spacing(1),
              mb: theme.spacing(2),
              fontFamily: customFont,
            }}
          >
            {feedbackTitle}
          </Typography>
          <TextField
            id="outlined-multiline-static"
            multiline
            rows={4}
            variant="outlined"
            placeholder={feedbackText}
            sx={{
              width: '96%',
            }}
            value={feedback}
            onChange={handleFeedbackChange}
          />
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              fontFamily: customFont,
              mb: theme.spacing(2),
              ml: theme.spacing(1),
              textAlign: 'left',
            }}
          >
            {feedback.length}/{maxFeedbackChars} {t('characters')}
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              color: 'white',
              width: 'fit-content',
              padding: theme.spacing(2),
              display: sendFeedback ? 'none' : 'inline-flex',
            }}
            onClick={() => sendNpsSurvey()}
          >
            {!sendFeedback ? t('send') : null}
          </Button>
          {sendFeedback && (
            <CheckCircleOutlineIcon
              fontSize={'large'}
              sx={{
                color: 'green',
                ...keyframes,
                animation: 'fadeIn 1s ease-out',
              }}
            />
          )}
        </Box>
      )}
      {contactEmail && !feedbackActive && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'fit-content',
            mb: theme.spacing(1),
            mt: theme.spacing(2),
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontFamily: customFont }}>{`${t(
            'appreciate_feedback'
          )}: ${contactEmail}`}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default NegativeFeedback;
